import * as React from "react";
import { AriaRadioProps, VisuallyHidden, useRadio } from "react-aria";
import { Flex } from "../flex";
import { Text } from "../text";
import { RadioContext } from "./radio";
import { Color, InputAppearance } from "../../utils/theme";

export interface RadioCardProps extends AriaRadioProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  showIndicator?: boolean;
  appearance?: InputAppearance;
  color?: Color;
}

export const RadioCard = React.forwardRef<HTMLLabelElement, RadioCardProps>(
  (
    {
      title,
      description,
      children,
      appearance,
      showIndicator = true,
      color,
      ...props
    },
    ref
  ) => {
    const state = React.useContext(RadioContext);
    if (!state) throw new Error("RadioCard must be used within a RadioGroup");

    const inputRef = React.useRef(null);
    const { inputProps } = useRadio(props, state, inputRef);

    return (
      <label
        data-radius="lg"
        className="p-radio-card"
        data-color={color}
        data-appearance={appearance}
        data-checked={inputProps.checked}
        ref={ref}
      >
        <VisuallyHidden>
          <input {...inputProps} ref={inputRef} />
        </VisuallyHidden>
        {showIndicator && (
          <div
            className="p-radio-card-indicator p-fake-radio"
            data-size="sm"
            data-checked={inputProps.checked}
          />
        )}
        <Flex stretchX className="p-radio-card-content" stack gap={"xs"}>
          {(title || description) && (
            <Flex gap={"4xs"} stack>
              {title && (
                <Text color={color} size="sm">
                  {title}
                </Text>
              )}
              {description && (
                <Text
                  color={color}
                  lineHeight={"tall"}
                  size="xs"
                  variant="lighter"
                >
                  {description}
                </Text>
              )}
            </Flex>
          )}
          {children}
        </Flex>
      </label>
    );
  }
);

RadioCard.displayName = "RadioCard";
